import queryString from 'querystring';
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import BadgeStatus from '../../components/elements/BadgeStatus';
import Button from '../../components/elements/Button';
import Card from '../../components/elements/Card';
import DataTable from '../../components/elements/DataTable';
import ScrollToTop from '../../components/elements/ScrollToTop';
import Tab from '../../components/elements/Tab';
import Text from '../../components/fields/Text';
import MerchantVerificationFilter from '../../components/fragments/MerchantVerificationFilter';
import IconPersonalCard from '../../components/icons/IconPersonalCard';
import { AppContext } from '../../contexts';
import { formatDateTime } from '../../utils/format';
import { useDebounce } from '../../utils/hooks';
import { getUserData } from '../../utils/storage';
import {
  fetchMerchantRegistrationList,
  setMerchantVerificationFilter,
  submitProcess,
} from './actions';
import { IS_OVER_LIMIT_ROWS_SET_DATA } from './constants';
import styles from './styles.scoped.css';

function VerificationRegistrationMerchant() {
  const { setBreadcrumbs } = useContext(AppContext);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const isLaAdmin = getUserData()?.isLinkAjaAdmin;
  const query = queryString.parse(location.search.replace('?', ''));

  const {
    data,
    meta,
    loading,
    filter,
    dataMerchantRegistrationDetail,
    isOverLimitRows,
    isLoadingMerchantRegistrationDetail = true,
  } = useSelector((state) => state.merchantVerification);

  const [tabItem, setTabItem] = useState('all');
  const [isMultipleProcess, setIsMultipleProcess] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);
  const [search, setSearch] = useState('');
  const debouncedFilter = useDebounce(filter, 500);

  useEffect(() => {
    if (
      dataMerchantRegistrationDetail &&
      !isLoadingMerchantRegistrationDetail
    ) {
      navigate('/merchant/detail-verifikasi-data-merchant');
    }
  }, [dataMerchantRegistrationDetail, isLoadingMerchantRegistrationDetail]);

  useEffect(() => {
    if(selectedRows.length > 20){
      dispatch({ type: IS_OVER_LIMIT_ROWS_SET_DATA,
        data: { showAlert: true, rowsSelected: selectedRows.length } });
    }else if(isOverLimitRows && selectedRows.length <= 20){
      dispatch({ type: IS_OVER_LIMIT_ROWS_SET_DATA,
        data: { showAlert: false, rowsSelected: selectedRows.length } });
    }
  }, [selectedRows]);

  const handleChangeTab = (val) => {
    const newFilter = {
      ...filter,
      registrationStatus: val === 'all' ? '' : val,
    };
    setIsMultipleProcess(false);
    setSelectedRows([]);
    setTabItem(val);
    dispatch(setMerchantVerificationFilter(newFilter));
    const newQuery = queryString.stringify({
      ...query,
      ...newFilter,
    });
    navigate(`?${newQuery}`);
  };

  const handleSelectedRow = (item) => {
    const rows = selectedRows.includes(item.registrationId)
      ? selectedRows.filter((value) => value !== item.registrationId)
      : [...selectedRows, item.registrationId];
    setSelectedRows(rows);
  };

  const handleMultipleProcess = () => {
    if (selectedRows.length) {
      const params = {
        registrationId: selectedRows,
      };
      dispatch(submitProcess(params));
    } else {
      setIsMultipleProcess(true);
    }
  };

  const fetchMerchantRegistrationData = (page = 1, initial = true) => {
    dispatch(fetchMerchantRegistrationList(page, search, filter, initial));
  };

  const handleProcess = (data) => {
    const params = {
      registrationId: [data?.registrationId],
    };
    dispatch(submitProcess(params));
  };

  const Actions = useCallback(
    ({ data }) => {
      const isProcess = data.registrationStatus === 'Perlu Diproses';
      return (
        <div className={styles.actions}>
          <Button
            className={styles['detail-btn']}
            onClick={() => handleProcess(data)}
            style={isProcess ? 'blue' : 'outlined'}
          >
            {isProcess ? 'Proses' : 'Detail'}
          </Button>
        </div>
      );
    },
    [styles]
  );

  const column = [
    {
      heading: 'Tanggal Registrasi',
      value: (val) => formatDateTime(val.createdAt),
      className: '',
    },
    {
      heading: 'Tanggal Status',
      value: (val) => formatDateTime(val.updatedAt),
      className: '',
    },
    { heading: 'Nama Pangkalan LPG 3 Kg', value: 'storeName', className: '' },
    {
      heading: 'Status',
      value: (val) => <BadgeStatus label={val.registrationStatus} />,
      className: '',
    },
    { heading: 'Nama Pemilik', value: 'ownerName', className: '' },
    { heading: 'Petinjau', value: 'reviewer', className: '' },
    {
      heading: 'Action',
      value: (val) => <Actions data={val} />,
      className: '',
    },
  ];

  const tabList = useMemo(
    () => [
      { label: 'Semua', value: 'all', info: '' },
      ...meta.summary.map((item) => ({
        label: item.registrationStatus,
        value: item.registrationStatus,
        info: item.total || '',
      })),
    ],
    [meta.summary]
  );

  // const allData = useMemo(
  //   () => meta.summary.map((item) => item.total).reduce((a, c) => a + c, 0),
  //   [meta]
  // );

  useEffect(() => {
    fetchMerchantRegistrationData();
  }, [debouncedFilter]);

  useEffect(() => {
    if (isLaAdmin) {
      navigate('/registrasi-merchant');
    }
  }, [isLaAdmin]);

  useEffect(() => {
    setBreadcrumbs(['Verifikasi Data Pangkalan LPG 3 Kg']);
    if (query.registrationStatus) {
      setTabItem(query.registrationStatus);
    }
  }, []);

  return (
    <main className={styles.root}>
      <Tab
        data={tabList}
        defaultValue={query.registrationStatus || tabItem}
        onChange={handleChangeTab}
      />
      <MerchantVerificationFilter />
      <Card className={styles['table-filter-container']}>
        <p>
          Menampilkan <strong>{meta.totalData}</strong> data
        </p>
        <div className={styles['filter-wrap']}>
          <div className={styles.filters}>
            <Text
              className={styles['search-bar']}
              hideLabel
              inputProps={{
                placeholder: 'Cari data...',
                value: search,
                icon: 'ic-search.svg',
                onChange: (e) => setSearch(e.target.value),
              }}
            />
          </div>
        </div>
      </Card>
      {tabItem === 'Perlu Diproses' ? (
        <div className={styles.multipleInfo}>
          <p style={{ visibility: isMultipleProcess ? 'visible' : 'hidden' }}>
            Dipilih: <strong>{selectedRows.length}</strong>
          </p>
          <Button
            className={styles.multipleButton}
            disabled={isMultipleProcess && selectedRows.length > 20}
            onClick={handleMultipleProcess}
          >
            {isMultipleProcess ? (
              <span>Proses</span>
            ) : (
              <>
                <IconPersonalCard />
                <span>Multiple Proses</span>
              </>
            )}
          </Button>
        </div>
      ) : null}
      <Card className={styles.table}>
        <div className={styles['table-container']}>
          <DataTable
            className={styles.table}
            column={column}
            data={data}
            endTreshould={500}
            isSelectable={isMultipleProcess}
            loading={loading}
            meta={meta}
            onEndReached={() =>
              fetchMerchantRegistrationData(meta.page + 1, false)
            }
            onRefresh={fetchMerchantRegistrationData}
            onSelectedRow={handleSelectedRow}
            withIndex
          />
        </div>
      </Card>
      <ScrollToTop />
    </main>
  );
}

export default VerificationRegistrationMerchant;
